/**
 * Raw Firestore document → model instance, with no Firestore SDK in sight.
 *
 * This is the ONE conversion every client read applies to a document:
 * `FirestoreRepository` feeds it snapshots from the browser SDK, and a page
 * that was server-rendered feeds it the same documents the server already read
 * (see `menuFromDocs.ts`). Keeping the two on one function is the point — a
 * second copy is how the seeded menu and the fetched menu would come to
 * disagree about a skipped document or a docId.
 *
 * Pure apart from logging: no `firebase/firestore` import, so it can be used
 * (and tested) without a Firestore instance.
 */
import { createLogger } from '../utils/logger';

const logger = createLogger('FirestoreRepository');

/**
 * A document as read: its id and its stored fields (without the id).
 * The browser SDK hands us `{ id, data() }`; a server seed arrives already in
 * this shape.
 */
export interface RawFirestoreDoc {
    docId: string;
    data: Record<string, any>;
}

/** Interface for a class that can convert raw data to a model instance. */
export interface IModelClass<T> {
    fromJSON(data: Record<string, any>): T;
}

/** A model plus its Firestore document ID. */
export type DocumentWithId<T> = T & { docId: string };

/**
 * One document → one model. STRICT: throws when `fromJSON` does.
 * The docId is attached after `fromJSON`, exactly as the repository always did.
 */
export function modelFromDoc<T>(modelClass: IModelClass<T>, doc: RawFirestoreDoc): DocumentWithId<T> {
    const model = modelClass.fromJSON(doc.data);
    (model as DocumentWithId<T>).docId = doc.docId;
    return model as DocumentWithId<T>;
}

/**
 * Map a whole collection, letting ONE malformed document be skipped instead
 * of taking the collection down with it.
 *
 * 🔴 THE FAILURE THIS FIXES, SEEN IN PRODUCTION. `fromJSON` throws on a
 * field it cannot coerce — `OrderItem.fromJSON: menuItemId must be a number
 * (got object: null)`. Inside `snapshot.docs.map(toModel)` that throw
 * escapes the map, the subscription callback, and every `try` above it: the
 * management orders list rendered nothing and logged an uncaught error.
 * ELEVEN items across NINE orders carried a null `menuItemId` (Manje Lakay,
 * first-party takeout, 2026-08-04 → 08-06), and those nine documents made
 * the other 300-odd unreadable.
 *
 * ⚠️ THE MODEL STAYS STRICT ON PURPOSE. Loosening `fromJSON` to accept null
 * would silently admit malformed money and identity fields everywhere,
 * including on the write path. The document is genuinely invalid; what was
 * wrong is that ONE invalid row was allowed to mean "this collection cannot
 * be read".
 *
 * ⚠️ IT IS NOT SILENT. Every skipped document is logged with its id and the
 * reason, and the count is logged once per collection — a filter that drops
 * rows quietly is how a list shows 300 orders instead of 309 and nobody
 * notices. Callers that need strictness can use `modelFromDoc` directly.
 *
 * @param source what was read, for the log: `context` names the operation
 *   (`find`, `subscribe`, `server seed`) and `collectionPath` the collection.
 */
export function modelsFromDocs<T>(
    modelClass: IModelClass<T>,
    docs: readonly RawFirestoreDoc[],
    source: { context: string; collectionPath: string },
): DocumentWithId<T>[] {
    const models: DocumentWithId<T>[] = [];
    const skipped: Array<{ docId: string; reason: string }> = [];
    for (const doc of docs) {
        try {
            models.push(modelFromDoc(modelClass, doc));
        } catch (error) {
            skipped.push({
                docId: doc.docId,
                reason: error instanceof Error ? error.message : String(error),
            });
        }
    }
    if (skipped.length > 0) {
        logger.error(
            `${source.context}: skipped ${skipped.length} malformed document(s) — ` +
                'they are excluded from this result and need repair',
            {
                collectionPath: source.collectionPath,
                total: docs.length,
                skipped,
            },
        );
    }
    return models;
}
